import React, {useState} from "react";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {Alert, Button, Chip, styled, TableCell, TableContainer, Tooltip, Typography} from "@mui/material";
import TableBody from "@mui/material/TableBody";
import {convertDateToTimeString} from "../utils/cert-helper";
import Box from "@mui/material/Box";
import {ApiUrl, PortalUrl} from "../utils/portal-urls";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import RunCircleRoundedIcon from '@mui/icons-material/RunCircleRounded';
import ReportRoundedIcon from '@mui/icons-material/ReportRounded';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import InfoIcon from '@mui/icons-material/Info';
import ContributionExternalSourceQueryDialog from "./ContributionExternalSourceQueryDialog";
import ContributionRuleFailureDialog from "./ContributionRuleFailureDialog";
import ContributionFieldFailureDialog from "./ContributionFieldFailureDialog";
import {statusObj} from "../utils/customFunctions";
import BinaryDialog from "./BinaryDialog";
import {Link} from "react-router-dom";

const StyledTableRow = styled(TableRow)(({theme}) => ({
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));
const StyledTableCell = styled(TableCell)({
    fontWeight: 'bold'
})

const StyledSpan = styled('span')({
    padding: '10px'
})
const StyledDetailDiv = styled('div')({
    '& .value': {
        color: '#1769aa',
    }
})
const StyledInfoButton = styled(Button)({
    textAlign: 'left'
})
const StyledTypography = styled(Typography)({
    fontSize: '0.875rem',
})
const StyledDetailSpan = styled('span')({
    fontSize: '0.75rem'
})
const acceptedObj = {
    accepted: {
        value: 'ACCEPTED', color: 'success'
    },
    rejected: {
        value: 'REJECTED', color: 'warning'
    }
}
const contributionTypeObj = {
    1: { value: 'FULL', color: '#3CB371' },      // MediumSeaGreen
    2: { value: 'ADD', color: '#FFA500' },      // Orange
    3: { value: 'INACTIVE', color: '#FF4500' },  // OrangeRed
   //4: { value: 'MERGE', color: '#FFA500' },   // Orange
}
const sortObjByValueDesc = (obj) => {
    return Object.entries(obj)
        .sort(([, a], [, b]) => b - a)
        .reduce((prev, [key, value]) => ({...prev, [key]: value}), {})
}

export default function ContributionsAsList(props) {
    const minimum = props.minimum || false;
    const contributions = props.contributions || [];
    const hispsLogo = props.hispsLogo;

    // External data source query info
    const [externalSourceQueryDialogOpen, setExternalSourceQueryDialogOpen] = useState(false);
    const [externalSourceQueryInfo, setExternalSourceQueryInfo] = useState(null);

    // rule failure map
    const [ruleFailureDialogOpen, setRuleFailureDialogOpen] = useState(false);
    const [ruleFailureMap, setRuleFailureMap] = useState(null);

    // field failure map
    const [fieldFailureDialogOpen, setFieldFailureDialogOpen] = useState(false);
    const [fieldFailureMap, setFieldFailureMap] = useState(null);

    // binaries dialog
    const [binaryDialogOpen, setBinaryDialogOpen] = useState(false);
    const [binaries, setBinaries] = useState(null);

    const handleStatusColor = (status) => {
        const statusIcon = () => {
            switch (status) {
                case 'Completed':
                    return <CheckCircleIcon/>
                case 'Init':
                case 'Running':
                    return <RunCircleRoundedIcon/>
                case 'Cancel':
                case 'Failed':
                    return <CancelRoundedIcon/>
                default:
                    return <ReportRoundedIcon/>
            }
        }

        const color = statusObj[status];
        if (color !== undefined) {
            return (
                <Chip icon={statusIcon(status)}
                      variant='outlined'
                      label={status.toUpperCase()}
                      color={color}
                      sx={{'& span': {paddingTop: '3px'}}}
                />
            )
        }
    }

    const handleAcceptedColor = (accepted) => {
        const obj = accepted ? acceptedObj.accepted : acceptedObj.rejected;

        return <Alert color={obj.color} icon={false}
                      sx={{display: 'flex', justifyContent: 'center', padding: 0}}>{obj.value}</Alert>

    }

    const handleContributionTypeColor = (contributionType) => {
        const obj = contributionTypeObj[contributionType];
        if(obj){
            return <div style={{backgroundColor: obj.color, fontWeight: 'bold'}}>{obj.value}</div>
        }
        return null;
    }

    const handleExternalSourceQueryDialogOpen = (info) => {
        setExternalSourceQueryInfo(info);
        setExternalSourceQueryDialogOpen(true);

    }

    const handleRuleFailureDialogOpen = (info) => {
        setRuleFailureMap(sortObjByValueDesc(info));
        setRuleFailureDialogOpen(true);
    }

    const handleFieldFailureDialogOpen = (info) => {
        setFieldFailureMap(sortObjByValueDesc(info));
        setFieldFailureDialogOpen(true);
    }

    const handleBinaryDialogOpen = (info) => {
        setBinaryDialogOpen(true);
        setBinaries(info);
    }
    console.log('contributions:::::', contributions);
    return (
        <TableContainer sx={{mt: '10px'}}>
            <ContributionExternalSourceQueryDialog
                setExternalSourceQueryDialogOpen={(d) => setExternalSourceQueryDialogOpen(d)}
                externalSourceQueryDialogOpen={externalSourceQueryDialogOpen}
                externalSourceQueryInfo={externalSourceQueryInfo}
            />
            <ContributionRuleFailureDialog
                setRuleFailureDialogOpen={(d) => setRuleFailureDialogOpen(d)}
                ruleFailureDialogOpen={ruleFailureDialogOpen}
                ruleFailureMap={ruleFailureMap}
            />
            <ContributionFieldFailureDialog
                setFieldFailureDialogOpen={(d) => setFieldFailureDialogOpen(d)}
                fieldFailureDialogOpen={fieldFailureDialogOpen}
                fieldFailureMap={fieldFailureMap}
            />
            <BinaryDialog
                binaryDialogOpen={binaryDialogOpen}
                setBinaryDialogOpen={(c) => setBinaryDialogOpen(c)}
                binaries={binaries}
            />
            <Table sx={{m: 0}}>
                <TableHead>
                    <TableRow>
                        <StyledTableCell>Hisp / Status</StyledTableCell>
                        <StyledTableCell>Details</StyledTableCell>
                        <StyledTableCell style={{width: '350px'}}>Validation Information</StyledTableCell>
                        <StyledTableCell>Submission/Publish Time</StyledTableCell>
                        {/*<StyledTableCell>Last Published Time</StyledTableCell>*/}
                        {!minimum && <StyledTableCell>Report</StyledTableCell>}

                    </TableRow>
                </TableHead>
                <TableBody>
                    {contributions && contributions.map(c => (

                        <StyledTableRow key={c.id}>
                            <TableCell sx={{pt: '24px', pb: '8px'}}>
                                <div style={{display: 'flex', flexDirection: 'column', textAlign: 'center'}}>
                                    {hispsLogo.hasOwnProperty(c.hisp) && <div><img src={hispsLogo[c.hisp]} alt={c.hisp}
                                                                                   style={{
                                                                                       maxHeight: 75,
                                                                                       maxWidth: 75
                                                                                   }}/></div>}
                                    <StyledTypography fontWeight='bold'>{c.hisp}</StyledTypography>
                                    {c.status === 'Completed'
                                        ? <StyledSpan>{handleAcceptedColor(c.accepted)}</StyledSpan>
                                        : <StyledSpan>{handleStatusColor(c.status)}</StyledSpan>
                                    }
                                    <StyledSpan>{handleContributionTypeColor(c.contributionType)}</StyledSpan>
                                </div>
                            </TableCell>
                            <TableCell>
                                <StyledDetailDiv>
                                    <StyledTypography>
                                        <span>Input Record Count</span>
                                        <br/>
                                        <span className='value'>{c.inputRecordCount}</span>
                                    </StyledTypography>
                                    {/* Shown only when something was actually omitted. omitted_row_count is
                                        NOT NULL DEFAULT 0, so every contribution processed before this
                                        feature also reads 0 — displaying those would assert "nothing was
                                        omitted" for rows where it was simply never measured. */}
                                    {c.omittedRowCount > 0 &&
                                        <StyledTypography>
                                            <span>Omitted Rows</span>
                                            <StyledDetailSpan>&nbsp;(not published)</StyledDetailSpan>
                                            <br/>
                                            <span className='value'>{c.omittedRowCount}</span>
                                        </StyledTypography>}
                                    <StyledTypography>
                                        <span>Error Count</span>
                                        <StyledDetailSpan>&nbsp;(Tolerated / Un-Tolerated)</StyledDetailSpan>
                                        <br/>
                                        <span
                                            className='value'>{c.toleratedErrorCount} / {c.errorCount - c.toleratedErrorCount} </span>
                                    </StyledTypography>
                                    <StyledTypography>
                                        <span>Warning Count</span>
                                        <StyledDetailSpan>&nbsp;(Tolerated / Un-Tolerated)</StyledDetailSpan>
                                        <br/>
                                        <span
                                            className='value'>{c.toleratedWarningCount} / {c.warningCount - c.toleratedWarningCount}</span>
                                    </StyledTypography>
                                    <StyledTypography>
                                        <span>AlertCount Count</span>
                                        <StyledDetailSpan>&nbsp;(Tolerated / Un-Tolerated)</StyledDetailSpan>
                                        <br/>
                                        <span
                                            className='value'>{c.toleratedAlertCount} / {c.alertCount - c.toleratedAlertCount}</span>
                                    </StyledTypography>
                                </StyledDetailDiv>
                            </TableCell>
                            <TableCell>
                                <div>ID: <span style={{color: '#777777'}}>{c.id}</span></div>
                                {c.detailsInfo &&
                                    <div>
                                        {c.detailsInfo.fatalErrorName &&
                                            <div>
                                                Fatal Error:
                                                <span style={{
                                                    color: '#e91e63',
                                                    fontWeight: 'bold'
                                                }}>{c.detailsInfo.fatalErrorName}</span>
                                                <br/>
                                            </div>}
                                        {c.detailsInfo.fatalErrorDescription &&
                                            <div>
                                                <span
                                                    style={{color: '#e91e63'}}>{c.detailsInfo.fatalErrorDescription}</span>
                                                <br/>
                                            </div>}
                                        {c.detailsInfo.numOfSubsets >= 0 &&
                                            <div>
                                                Numbers of Subsets:
                                                <span style={{color: '#1769aa'}}>{c.detailsInfo.numOfSubsets}</span>
                                                <br/>
                                            </div>}
                                        {c.detailsInfo.numOfValidationFailure > 0 &&
                                            <div>
                                                Numbers of Validation Failure:
                                                <span
                                                    style={{color: '#e91e63'}}>{c.detailsInfo.numOfValidationFailure} exception(s). Download the HISP Exception Report to view details</span>
                                                <br/>
                                            </div>}
                                        {c.detailsInfo.externalDataSourceQueryInfo &&
                                            <div>
                                                <StyledInfoButton
                                                    variant='text'
                                                    size='small'
                                                    startIcon={<InfoIcon/>}
                                                    onClick={() => handleExternalSourceQueryDialogOpen(c.detailsInfo.externalDataSourceQueryInfo)}
                                                    sx={{mt: 2}}
                                                >
                                                    External DataSource Query Info
                                                </StyledInfoButton>
                                            </div>}
                                        {c.detailsInfo.ruleFailureMap &&
                                            <div>
                                                <StyledInfoButton
                                                    variant='text'
                                                    size='small'
                                                    startIcon={<InfoIcon/>}
                                                    onClick={() => handleRuleFailureDialogOpen(c.detailsInfo.ruleFailureMap)}
                                                >
                                                    Validation Issues Info
                                                </StyledInfoButton>
                                            </div>}
                                        {c.detailsInfo.fieldFailureMap &&
                                            <div>
                                                <StyledInfoButton
                                                    variant='text'
                                                    size='small'
                                                    startIcon={<InfoIcon/>}
                                                    onClick={() => handleFieldFailureDialogOpen(c.detailsInfo.fieldFailureMap)}
                                                >
                                                    Primary Field Validation Issue Info
                                                </StyledInfoButton>
                                            </div>}
                                    </div>
                                }
                            </TableCell>
                            <TableCell>
                                <StyledDetailDiv>
                                    <div>
                                        Submission Time<br/>
                                        <span
                                            style={{color: '#1769aa'}}>{convertDateToTimeString(c.submissionTs)}</span>
                                    </div>
                                    {c.lastPublishedTs &&
                                        <div>
                                            Last Published Time<br/>
                                            <span
                                                style={{color: '#1769aa'}}>{convertDateToTimeString(c.lastPublishedTs)}</span>
                                        </div>
                                    }
                                </StyledDetailDiv>
                            </TableCell>
                            {/*<TableCell>{convertDateToTimeString(c.submissionTs)}</TableCell>*/}
                            {/*<TableCell>{c.lastPublishedTs === null ? "None" : convertDateToTimeString(c.lastPublishedTs)}</TableCell>*/}
                            {!minimum && <TableCell>
                                <Box>
                                    {c.detailedReportDataPath &&
                                        <div>
                                            <Tooltip arrow title='Download Detail Report' placement='left'>
                                                <Button sx={{textTransform: 'none'}}
                                                        href={ApiUrl.Contributions.getDetailedReport.getPath(c.id)}
                                                        target='_blank' startIcon={<FileDownloadIcon/>}>
                                                    HISP Detail Report
                                                </Button>
                                            </Tooltip>
                                        </div>}
                                    {c.summaryReportDataPath &&
                                        <div>
                                            <Tooltip title='Download Summary Report' arrow placement='left'>
                                                <Button sx={{textTransform: 'none'}}
                                                        href={ApiUrl.Contributions.getSummaryReport.getPath(c.id)}
                                                        target="_blank" startIcon={<FileDownloadIcon/>}>
                                                    HISP Summary Report
                                                </Button>
                                            </Tooltip>
                                        </div>}
                                    {/* The two conditional buttons sit together on purpose: their
                                        presence is itself a signal about the contribution — rows were
                                        dropped, or checks failed to run. Input goes last, being the
                                        one that is always there and rarely what an admin came for. */}
                                    {c.omittedRowsDataPath &&
                                        <div>
                                            <Tooltip arrow
                                                     title='Download the rows that did not reach the directory'
                                                     placement='left'>
                                                <Button sx={{textTransform: 'none'}} color="warning"
                                                        href={ApiUrl.Contributions.getOmittedRows.getPath(c.id)}
                                                        target='_blank' startIcon={<FileDownloadIcon/>}>
                                                    Omitted Rows
                                                </Button>
                                            </Tooltip>
                                        </div>}
                                    {c.exceptionReportDataPath &&
                                        <div>
                                            <Tooltip arrow title='Rules that failed to run — these rows were not fully checked'
                                                     placement='left'>
                                                <Button sx={{textTransform: 'none'}} color="error"
                                                        href={ApiUrl.Contributions.getExceptionReport.getPath(c.id)}
                                                        target='_blank' startIcon={<FileDownloadIcon/>}>
                                                    HISP Exception Report
                                                </Button>
                                            </Tooltip>
                                        </div>}
                                    {c.inputDataPath &&
                                        <div>
                                            <Tooltip title='Download the file as the HISP submitted it' arrow placement='left'>
                                                <Button sx={{textTransform: 'none'}}
                                                        href={ApiUrl.Contributions.getInputFile.getPath(c.id)}
                                                        target="_blank" startIcon={<FileDownloadIcon/>}>
                                                    HISP Input File
                                                </Button>
                                            </Tooltip>
                                        </div>
                                    }
                                    {c.binaryCount > 0 &&
                                        <div>
                                            <Button component={Link}
                                                    to={PortalUrl.Contributions.HocReport.getPath(c.id)}>
                                                View AdHoc Report
                                            </Button>
                                        </div>
                                    }


                                    {/*<br/>*/}
                                    {/*{c.binaries?.length > 0 &&*/}
                                    {/*<Button sx={{ textTransform: 'none'}}*/}
                                    {/*        onClick={() => handleBinaryDialogOpen(c.binaries)}*/}
                                    {/*        startIcon={<AssignmentIcon />}>*/}
                                    {/*    Binaries*/}
                                    {/*</Button>}*/}
                                </Box>
                            </TableCell>
                            }

                        </StyledTableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
}
